import { Box } from '@mui/material'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { UseFormReset } from 'react-hook-form'
import { matchPath, useLocation, useNavigate } from 'react-router-dom'
import { SaveStatusRow } from './SaveStatusRow'
import { InstallationFormModel } from 'src/Libs/flow/installation/forms'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useFetchInstallation } from 'src/Libs/flow/installation/hooks'
import { syncInstallation } from 'src/Libs/flow/installation/api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { InstallationRequestBatch, SaveStatus, saveStatus } from '../../types'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { installationPaths } from 'src/Libs/flow/advanced-settings/constants'
import { isFlowTheme } from 'src/Libs/utils'
import { ApiResult, usePortalMutation } from 'src/Libs/api'
import { useActiveInstallation } from 'src/Hooks'

//#region InstallationSaveDialog
export interface InstallationSaveDialogProps {
  requests: InstallationRequestBatch
  reset: UseFormReset<InstallationFormModel>
  shouldSync: boolean
  onClose?: () => void
}

export const InstallationSaveDialog: React.FC<InstallationSaveDialogProps> = ({ requests, reset, shouldSync, onClose }) => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const location = useLocation()
  const navigate = useNavigate()

  const {
    installationIdRequest,
    installationRequests,
    emcIdRequest,
    emcRequests,
    allStorageRequests,
    controlledSubDeviceRequests,
    meterSubDeviceRequests,
    controllerSubDeviceRequests,
  } = requests

  /**
   * Counter for the number of operations that have not finished yet.
   * Goes down by one each time an operation finishes.
   */
  const [operationsPending, setOperationsPending] = useState(() => getRequestAmount(requests))
  const countOperationFinished = useCallback((): void => setOperationsPending(operations => operations - 1), [])

  /**
   * Stop and allow user to close the popup if all requests are finished, or an error has caused the operation to stop.
   * For example, if an installation is not created, there is no installation id for the other requests.
   */
  const stopOperation = (): void => setOperationsPending(0)
  const operationsFinished = operationsPending <= 0

  const [isSyncPending, setIsSyncPending] = useState(shouldSync)

  const [installationId, setInstallationId] = useState(installationIdRequest.installationId)
  const [emcId, setEmcId] = useState(emcIdRequest?.emcId ?? null)

  const queryClient = useQueryClient()

  const isNewInstallation = installationIdRequest.installationId === null

  const { refreshInstallation } = useActiveInstallation()

  const hasFired = useRef(false)
  useEffect(() => {
    if (operationsFinished && !hasFired.current) {
      hasFired.current = true
      queryClient.invalidateQueries({ queryKey: ['installation'] })

      if (isNewInstallation) {
        queryClient.refetchQueries({ queryKey: ['installations'] })
        queryClient.refetchQueries({ queryKey: ['installationsCount'] })
      } else if (installationId !== null) {
        refreshInstallation(installationId.toString()).catch(console.error)
      }
    }
  }, [queryClient, operationsFinished, isNewInstallation, installationId, refreshInstallation])

  const { installation: newInstallationValues } = useFetchInstallation(installationId ?? undefined, operationsFinished)

  const isInstallationPending = !!useIsFetching({ queryKey: ['installation', installationId] })

  const canClose = operationsFinished && !isSyncPending && !isInstallationPending && !!newInstallationValues

  const handleCloseDialog = (): void => {
    if (!canClose) return

    const pathname = location.pathname
    if (matchPath('/new-installation/*', pathname) && installationId !== null) {
      if (isFlowTheme) {
        navigate(`/installation/${installationId}/properties/advanced-settings/${installationPaths.General}`)
      } else {
        const installationPath = pathname.replace('new-installation', `installation/${installationId}/settings`)
        navigate(installationPath)
      }
    } else {
      reset(newInstallationValues)
    }

    onClose?.()
  }

  return (
    <Dialog
      title={t('installation.save.title')}
      onClose={handleCloseDialog}
      maxWidth={'md'}
      actions={{
        primary: {
          name: t('common.ok'),
          onClick: handleCloseDialog,
          disabled: !canClose,
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {!!installationIdRequest.installationRequest && (
          <SaveRow
            name={installationIdRequest.message}
            request={installationIdRequest.installationRequest}
            onSuccess={newInstallationId => setInstallationId(newInstallationId)}
            onError={stopOperation}
            onSettled={countOperationFinished}
          />
        )}
        {installationRequests.map(installationRequest => (
          <SaveRow
            key={installationRequest.uid}
            name={installationRequest.message}
            disabled={installationId === null}
            request={() => installationRequest.request(installationId!)}
            onSettled={countOperationFinished}
          />
        ))}

        {!!emcIdRequest?.emcRequest && (
          <SaveRow
            name={emcIdRequest.message}
            disabled={installationId === null}
            request={() => emcIdRequest.emcRequest(installationId!)}
            onSuccess={newEmcId => setEmcId(newEmcId.toString())}
            onError={stopOperation}
            onSettled={countOperationFinished}
          />
        )}
        {emcRequests.map(emcRequest => (
          <SaveRow
            key={emcRequest.uid}
            name={emcRequest.message}
            disabled={installationId === null || !emcId}
            request={() => emcRequest.request(installationId!, emcId!)}
            onSettled={countOperationFinished}
          />
        ))}

        {allStorageRequests.map(({ storageIdRequest, storageRequests }, index) => (
          <StorageRequestRows
            key={`${storageIdRequest?.storageId}-${index}`}
            storageIdRequest={storageIdRequest}
            storageRequests={storageRequests}
            installationId={installationId}
            emcId={emcId}
            setOperationsPending={setOperationsPending}
            countOperationFinished={countOperationFinished}
          />
        ))}

        {controlledSubDeviceRequests.map(request => (
          <SaveRow
            key={request.uid}
            name={request.message}
            disabled={installationId === null || !emcId}
            request={() => request.request(installationId!, emcId!)}
            onSettled={countOperationFinished}
          />
        ))}

        {meterSubDeviceRequests.map(request => (
          <SaveRow
            key={request.uid}
            name={request.message}
            disabled={installationId === null || !emcId}
            request={() => request.request(installationId!, emcId!)}
            onSettled={countOperationFinished}
          />
        ))}

        {controllerSubDeviceRequests.map(request => (
          <SaveRow
            key={request.uid}
            name={request.message}
            disabled={installationId === null || !emcId}
            request={() => request.request(installationId!, emcId!)}
            onSettled={countOperationFinished}
          />
        ))}

        {shouldSync && installationId && (
          <SaveRow
            name={t('installation.save.synchronization')}
            request={() => syncInstallation(installationId, authFetch)}
            onSettled={() => setIsSyncPending(false)}
            loadingLabel={t('installation.sync.status.inProgress')}
            completeLabel={t('installation.sync.status.done')}
            disabled={!operationsFinished}
          />
        )}
      </Box>
    </Dialog>
  )
}
//#endregion

//#region SaveRow component
type SaveRowProps<T> = {
  name: string
  request: () => Promise<T>
  onSuccess?: (data: T) => void
  onError?: () => void
  onSettled: () => void
  disabled?: boolean
  /** Custom label to display in the loading status chip instead of the default one. */
  loadingLabel?: string
  /** Custom label to display in the complete status chip instead of the default one. */
  completeLabel?: string
}

// TODO: Check PropType
/* eslint-disable-next-line  */
const SaveRow: React.FC<SaveRowProps<any>> = ({ name, request, onSuccess, onError, onSettled, disabled, loadingLabel, completeLabel }) => {
  const { t } = useCustomTranslation()
  const [status, setStatus] = useState<SaveStatus>(saveStatus.idle)
  const [customError, setCustomError] = useState('')

  const { mutate, error, isIdle } = usePortalMutation({
    mutationFn: async () => new ApiResult(await request()),
    onMutate: () => {
      setStatus(saveStatus.loading)
    },
    onError: e => {
      setStatus(saveStatus.error)
      onError?.()

      const errorData = (e as AxiosError<any>)?.response?.data // eslint-disable-line
      if (errorData?.errno === 405) {
        if (typeof errorData?.message === 'string') {
          try {
            const message = JSON.parse(errorData?.message)
            setCustomError(
              t('errors.installation.deviceExists', '', {
                installationId: message?.deviceLink?.installationId,
                deviceId: message?.deviceLink?.deviceId,
                deviceShortUuid: message?.deviceLink?.deviceShortUuid,
              })
            )
          } catch (_error) {
            setCustomError(
              t('errors.installation.deviceExists', '', {
                installationId: errorData?.message?.deviceLink?.installationId,
                deviceId: errorData?.message?.deviceLink?.deviceId,
                deviceShortUuid: errorData?.message?.deviceLink?.deviceShortUuid,
              })
            )
          }
        } else {
          setCustomError(
            t('errors.installation.deviceExists', '', {
              installationId: errorData?.message?.deviceLink?.installationId,
              deviceId: errorData?.message?.deviceLink?.deviceId,
              deviceShortUuid: errorData?.message?.deviceLink?.deviceShortUuid,
            })
          )
        }
      }
    },
    onSuccess: data => {
      setStatus(saveStatus.complete)
      onSuccess?.(data)
    },
    onSettled,
  })

  // Workaround to send the request once, even during development.
  const hasFired = useRef(false)
  useEffect(() => {
    if (!disabled && isIdle && !hasFired.current) {
      hasFired.current = true
      mutate()
    }
  }, [disabled, isIdle, mutate])

  return (
    <SaveStatusRow
      name={name}
      status={status}
      errorMessage={customError || error?.message}
      disabled={disabled ?? false}
      loadingLabel={loadingLabel}
      completeLabel={completeLabel}
    />
  )
}

/** Get the total amount of requests in the installation request batch. */
const getRequestAmount = (requests: InstallationRequestBatch): number => {
  const {
    installationIdRequest,
    installationRequests,
    emcIdRequest,
    emcRequests,
    allStorageRequests,
    controlledSubDeviceRequests,
    meterSubDeviceRequests,
    controllerSubDeviceRequests,
  } = requests

  let amount = 0

  amount += Number(!!installationIdRequest.installationRequest)
  amount += installationRequests.length

  amount += Number(!!emcIdRequest?.emcRequest)
  amount += emcRequests.length

  allStorageRequests.forEach(({ storageIdRequest, storageRequests }) => {
    amount += Number(!!storageIdRequest?.storageRequest)
    amount += storageRequests.length
  })

  amount += controlledSubDeviceRequests.length
  amount += meterSubDeviceRequests.length
  amount += controllerSubDeviceRequests.length

  return amount
}

//#region StorageRequestRows component
const StorageRequestRows: React.FC<
  InstallationRequestBatch['allStorageRequests'][number] & {
    installationId: number | null
    emcId: string | null
    setOperationsPending: React.Dispatch<React.SetStateAction<number>>
    countOperationFinished: () => void
  }
> = ({ installationId, emcId, setOperationsPending, countOperationFinished, storageIdRequest, storageRequests }) => {
  const [storageId, setStorageId] = useState(storageIdRequest?.storageId || null)

  return (
    <>
      {!!storageIdRequest?.storageRequest && (
        <SaveRow
          name={storageIdRequest.message}
          disabled={installationId === null || !emcId}
          request={() => storageIdRequest.storageRequest(installationId!)}
          onSuccess={newStorageId => {
            if (newStorageId) setStorageId(newStorageId.toString())
          }}
          onError={() => {
            // Cancel (remove from counter) requests that depend on storageId
            setOperationsPending(operations => operations - storageRequests.length)
          }}
          onSettled={countOperationFinished}
        />
      )}
      {storageRequests.map(storageRequest => (
        <SaveRow
          key={storageRequest.uid}
          name={storageRequest.message}
          disabled={installationId === null || !storageId}
          request={() => storageRequest.request(installationId!, storageId!)}
          onSettled={countOperationFinished}
        />
      ))}
    </>
  )
}
